import { forwardRef, type MouseEvent, useCallback, useMemo } from "react";
import {
  Link,
  parsePath,
  type LinkProps,
  useLinkClickHandler,
  useLocation,
} from "react-router-dom";

type LinkWithSearchProps = Omit<LinkProps, "to"> & {
  path: string;
  search?: string;
};

export const LinkWithSearch = forwardRef<HTMLAnchorElement, LinkWithSearchProps>(
  (
    {
      path,
      search,
      onClick,
      reloadDocument,
      replace,
      state,
      target,
      preventScrollReset,
      relative,
      viewTransition,
      ...props
    },
    ref,
  ) => {
    const location = useLocation();
    const to = useMemo(() => {
      const parsedPath = parsePath(path);

      return {
        ...parsedPath,
        search: search ?? parsedPath.search ?? location.search,
      };
    }, [location.search, path, search]);
    const handleLinkClick = useLinkClickHandler(to, {
      preventScrollReset,
      relative,
      replace,
      state,
      target,
      viewTransition,
    });
    const handleClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);

        if (!event.defaultPrevented && !reloadDocument) {
          handleLinkClick(event);
        }
      },
      [handleLinkClick, onClick, reloadDocument],
    );

    return <Link {...props} to={to} onClick={handleClick} ref={ref} target={target} />;
  },
);

LinkWithSearch.displayName = "LinkWithSearch";
